import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { ApiResponse, PaginatedResponse } from '@/types';
import { getActiveTenantId } from '@/store/tenantSelection';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || 'https://mensageria.grupoeta.com.br/api',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor para adicionar auth token
    this.client.interceptors.request.use(
      (config) => {
        const correlationId = `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
        (config.headers as any)['x-correlation-id'] = correlationId;
        (config as any).metadata = { startTime: performance.now(), correlationId };

        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        const tenantId = getActiveTenantId();
        if (tenantId) {
          config.headers['x-tenant-id'] = tenantId;
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor para tratamento de erros
    this.client.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          // Token expirado - redirecionar para login
          localStorage.removeItem('auth_token');
          window.location.href = '/login';
        }
        if (error.response?.data?.error) {
          error.message = error.response.data.error;
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.client.get(url, { params });
    return response.data;
  }

  async post<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.client.post(url, data);
    return response.data;
  }

  async put<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.client.put(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.client.delete(url);
    return response.data;
  }

  async getPaginated<T>(url: string, params?: Record<string, any>): Promise<ApiResponse<PaginatedResponse<T>>> {
    const response: AxiosResponse<ApiResponse<PaginatedResponse<T>>> = await this.client.get(url, { params });
    return response.data;
  }
}

export const apiClient = new ApiClient();
